"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import PortalNavBar from "../components/PortalNavBar";
import { backendFetch } from "../lib/backend";

type Membership = {
  agreementId: number;
  agreementAddress: string;
  agreementRole: string;
  active: boolean;
};

type Schedule = {
  frequency: string;
  emailEnabled: boolean;
  nextDueAt: string;
} | null;

const FREQUENCIES = [
  { value: "OFF", label: "No reminders" },
  { value: "DAILY", label: "Every day" },
  { value: "WEEKLY", label: "Every week" },
  { value: "BIWEEKLY", label: "Every 2 weeks" },
  { value: "MONTHLY", label: "Every month (30 days)" },
];

export default function DistributionControlsPage() {
  const { ready, authenticated, login, getAccessToken } = usePrivy();
  const { wallets } = useWallets();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [selectedAgreementId, setSelectedAgreementId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<Schedule>(null);
  const [frequency, setFrequency] = useState("OFF");
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [due, setDue] = useState(false);
  const [availableBalance, setAvailableBalance] = useState("0.00");
  const [savingSchedule, setSavingSchedule] = useState(false);

  const navLinks = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/account", label: "Account" },
    { href: "/accounts-breakdown", label: "Accounts Breakdown" },
    { href: "/agpsira-checkout-app", label: "AGPSIRA Checkout App" },
    { href: "/revenue-history", label: "Revenue History" },
    { href: "/earnings-history", label: "Earnings History" },
    { href: "/sales-history", label: "Sales History" },
    { href: "/distribution-controls", label: "Distribution Controls", active: true },
  ];

  useEffect(() => {
    if (!ready || !authenticated) return;
    let active = true;

    async function loadAgreements() {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Sign in again to load your agreements.");
        const response = await backendFetch("/api/participant-access/me", token);
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "Could not load agreements.");
        }
        const pseAgreements = (data.memberships ?? []).filter(
          (membership: Membership) => membership.active && membership.agreementRole === "PSE"
        );
        if (active) {
          setMemberships(pseAgreements);
          setSelectedAgreementId(pseAgreements[0]?.agreementId ?? null);
        }
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : "Could not load agreements.");
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadAgreements();
    return () => {
      active = false;
    };
  }, [authenticated, getAccessToken, ready]);

  const loadSchedule = useCallback(async () => {
    if (selectedAgreementId === null) return;
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await backendFetch(
        `/api/revenue/${selectedAgreementId}/distribution-schedule`,
        token
      );
      const data = await response.json();
      if (!response.ok || !data.success) return;
      setSchedule(data.schedule);
      setFrequency(data.schedule?.frequency ?? "OFF");
      setEmailEnabled(data.schedule?.emailEnabled ?? true);
      setDue(Boolean(data.due));
      setAvailableBalance(data.availableBalance ?? "0.00");
    } catch {
      // The schedule is optional; distributions still work without it.
    }
  }, [selectedAgreementId, getAccessToken]);

  useEffect(() => {
    if (ready && authenticated) void loadSchedule();
  }, [ready, authenticated, loadSchedule]);

  async function saveSchedule() {
    if (selectedAgreementId === null) return;
    try {
      setSavingSchedule(true);
      setError(null);
      setMessage(null);
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in again to save the schedule.");
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000"}/api/revenue/${selectedAgreementId}/distribution-schedule`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ frequency, emailEnabled }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Could not save the schedule.");
      setMessage(frequency === "OFF" ? "Distribution reminders turned off." : "Distribution reminder schedule saved.");
      await loadSchedule();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Could not save the schedule.");
    } finally {
      setSavingSchedule(false);
    }
  }

  async function executeDistribution() {
    if (selectedAgreementId === null) return;
    try {
      setDistributing(true);
      setError(null);
      setMessage(null);
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in again to distribute revenue.");
      const response = await backendFetch(
        `/api/revenue/${selectedAgreementId}/distribution`,
        token
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Could not prepare the distribution.");
      }

      const wallet = wallets.find(
        (candidate) => candidate.address.toLowerCase() === data.transaction.to?.toLowerCase()
      ) ?? wallets[0];
      const provider = await wallet?.getEthereumProvider();
      if (!wallet || !provider) throw new Error("Your Privy wallet is unavailable.");

      const hash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet.address, to: data.transaction.to, data: data.transaction.data }],
      });
      setMessage(`Distribution submitted on Polygon: ${String(hash)}. Waiting for confirmation...`);

      // The backend waits for the transaction to be mined, then marks the revenue as distributed.
      let confirmed = false;
      for (let attempt = 0; attempt < 20 && !confirmed; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const confirmResponse = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000"}/api/revenue/${selectedAgreementId}/distribution/confirm`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ transactionHash: String(hash) }),
          }
        );
        confirmed = confirmResponse.ok;
      }
      setMessage(
        confirmed
          ? `Distribution confirmed: ${String(hash)}. Revenue History now shows it as distributed.`
          : `Distribution submitted (${String(hash)}) but not yet confirmed. Revenue History will update automatically once it is mined.`
      );
      await loadSchedule();
    } catch (distributionError) {
      setError(distributionError instanceof Error ? distributionError.message : "Distribution failed.");
    } finally {
      setDistributing(false);
    }
  }

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white">
        <button type="button" onClick={login} className="rounded-xl bg-blue-600 px-5 py-3 font-semibold">
          Sign in to distribute agreement revenue
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
              PSE / Client Portal
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-3">Distribution Controls</h1>
            <p className="text-slate-300 text-lg max-w-3xl">
              Allocate the agreement&apos;s available USDC to its participants according to the contract shares.
            </p>
          </div>

          <Link
            href="/dashboard"
            className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500"
          >
            Back to dashboard
          </Link>
        </div>

        <PortalNavBar links={navLinks} portalTitle="Participant Navigation" />

        <div className="max-w-3xl border-t border-slate-800 py-8">
          <label htmlFor="agreement" className="mb-2 block text-sm text-slate-300">PSE agreement</label>
          <select
            id="agreement"
            value={selectedAgreementId ?? ""}
            onChange={(event) => setSelectedAgreementId(Number(event.target.value))}
            disabled={loading || memberships.length === 0 || distributing}
            className="mb-5 w-full max-w-xl border border-slate-700 bg-slate-900 px-4 py-3 text-white"
          >
            {memberships.map((membership) => (
              <option key={membership.agreementId} value={membership.agreementId}>
                Agreement #{membership.agreementId} · {membership.agreementAddress}
              </option>
            ))}
          </select>

          {due && (
            <div role="alert" className="mb-5 rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 text-sm text-amber-200">
              Your distribution is due. {availableBalance} USDC is waiting to be distributed.
            </div>
          )}

          {memberships.length === 0 && !loading && <p className="mb-4 text-sm text-slate-400">No active PSE agreements are linked to this wallet.</p>}
          <button
            type="button"
            onClick={() => void executeDistribution()}
            disabled={loading || memberships.length === 0 || distributing}
            className="inline-flex items-center gap-2 bg-emerald-700 px-4 py-3 font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {distributing ? "Confirm in Privy..." : "Distribute available USDC"}
          </button>

          <div className="mt-10 border-t border-slate-800 pt-8">
            <h2 className="mb-1 text-xl font-semibold">Distribution reminders</h2>
            <p className="mb-4 text-sm text-slate-400">
              Choose how often you want to be reminded. When the interval passes and USDC is waiting, you get an email
              and a notice here, then sign the distribution yourself. Nothing is distributed automatically.
            </p>
            <div className="grid max-w-xl gap-3">
              <select
                value={frequency}
                onChange={(event) => setFrequency(event.target.value)}
                disabled={selectedAgreementId === null || savingSchedule}
                className="border border-slate-700 bg-slate-900 px-4 py-3 text-white"
              >
                {FREQUENCIES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(event) => setEmailEnabled(event.target.checked)}
                  disabled={frequency === "OFF"}
                />
                Also email me when a distribution is due
              </label>
              <button
                type="button"
                onClick={() => void saveSchedule()}
                disabled={selectedAgreementId === null || savingSchedule}
                className="w-fit border border-blue-500/50 bg-blue-600/20 px-4 py-2 font-semibold text-blue-100 hover:bg-blue-600 disabled:opacity-50"
              >
                {savingSchedule ? "Saving..." : "Save schedule"}
              </button>
              {schedule && (
                <p className="text-sm text-slate-400">
                  Next distribution due: {new Date(schedule.nextDueAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {message && <p role="status" className="mt-4 text-sm text-emerald-300">{message}</p>}
          {error && <p role="alert" className="mt-4 text-sm text-red-300">{error}</p>}
        </div>
      </section>
    </main>
  );
}
